import type { DayView } from '../../shared/api';
import type { NutrientDef } from '../../shared/nutrients';
import { amount, colorFor, defFor, directionWord, progress, unitLabel } from '../lib/format';

interface Props {
  day: DayView;
  defs: Map<string, NutrientDef>;
}

const MACROS = ['protein', 'carbs', 'fat'] as const;

/** Protein, carbs and fat against their targets. Without targets (counting without goals), just the grams eaten. */
export function MacroBars({ day, defs }: Props) {
  return (
    <div className="macros">
      {MACROS.map((key) => {
        const target = day.targets.find((t) => t.key === key);
        const amount = target?.amount ?? null;
        const eaten = day.totals[key] ?? 0;
        const def = defFor(key, defs);
        const p = target ? progress(target, eaten) : { pct: 0, state: 'ok' as const };
        return (
          <div key={key}>
            <div className="macro__top">
              <span className="macro__name">
                <span className="macro__swatch" style={{ background: colorFor(key) }} />
                {def.name}
              </span>
              <span className="num">
                <b>{Math.round(eaten)}</b>
                <span className="muted">{amount !== null ? ` / ${amount} g` : ' g'}</span>
              </span>
            </div>
            {/* A bar only measures against a target: with none, the grams say it all. */}
            {amount !== null && (
              <div className="bar" role="meter" aria-label={`${def.name}`} aria-valuemin={0} aria-valuemax={amount} aria-valuenow={Math.round(eaten)}>
                <div className="bar__fill" style={{ width: `${Math.min(100, p.pct * 100)}%`, background: colorFor(key) }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Opt-in nutrients the person chose to track (fiber, sodium, vitamin D, their own…). */
export function NutrientChips({ day, defs }: Props) {
  const extras = day.targets.filter((t) => !['kcal', 'protein', 'carbs', 'fat'].includes(t.key));
  if (extras.length === 0) return null;
  return (
    <div className="nchips">
      {extras.map((t) => {
        const def = defFor(t.key, defs);
        const eaten = day.totals[t.key] ?? 0;
        const known = day.known[t.key] ?? 0;
        const partial = day.entryCount > 0 && known < day.entryCount;
        const p = progress(t, eaten);
        const color =
          p.state === 'over' ? 'var(--bad)' : p.state === 'near' ? 'var(--warn)' : t.direction === 'min' && p.state === 'ok' && eaten > 0 ? 'var(--good)' : colorFor(t.key);
        return (
          <div className="nchip" key={t.key} title={partial ? `${known} of ${day.entryCount} foods have ${def.name} data` : undefined}>
            <div className="nchip__top">
              <span className="nchip__name">{def.name}</span>
              <span className="num muted">
                {partial && '≥'}
                <b style={{ color: 'var(--ink)' }}>{amount(eaten, def)}</b>
                {t.amount !== null && ` / ${amount(t.amount, def)}`} {unitLabel(def)}
              </span>
            </div>
            <div className="bar" aria-hidden="true">
              <div className="bar__fill" style={{ width: `${Math.min(100, p.pct * 100)}%`, background: color }} />
            </div>
            <div className="tiny faint">
              {directionWord(t.direction)}
              {partial ? ` · ${day.entryCount - known} without data` : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
}
